import React, { useEffect, useState } from "react";
import styles from "./UtilityBar.module.scss";
import {
  ButtonShape,
  ButtonSize,
  ButtonType,
} from "../../../enums/ButtonAttributesEnums";
import TNButton from "../../../reusable-components/TNButton";
import { InputfieldTypes } from "../../../enums/InputFieldAttributesEnums";
import InputField from "../../../reusable-components/TNInputField";
import SearchIcon from "../../../assets/images/search-button.svg";
import FilterIcon from "../../../assets/images/filterIcon.svg";
import { TriggerType } from "../../../enums/TriggerTypeEnums";
import { utilityBarConsts } from "./constants";
import { useFormContext } from "react-hook-form";
import { ButtonGroupStyles } from "../../InvoiceManagement/UtilityBar/constants";
import { ViewListingTypes } from "../../../enums/ViewListingTypes";
import clsx from "clsx";
import ListViewIcon from "../../../assets/images/listViewIcon.svg";
import MapViewIcon from "../../../assets/images/mapViewIcon.svg";
import { QueryKeys } from "../../../constants/QueryKeys";
import ConditionalRender from "../../../reusable-components/ConditionalRender";
import SocketLoader from "../../../reusable-components/SocketLoader";
import { socketActionType } from "../../../enums/socketEnums";
import { message } from "antd";
import QuarryDatabaseService from "../../../services/quarryDatabase.service";
import { useQueryClient } from "@tanstack/react-query";
import { useQuarryDatabaseContext } from "../../../context/QuarryDatabaseFilterContext";

interface UtilityBarProps {
  handleFilterDrawerOpen?: () => void;
  handleAddDrawerOpen?: () => void;
  activeView?: ViewListingTypes;
  handleViewToggle?: () => void;
  filterCount?: number;
  handleClearAllFilter?: () => void;
}

const UtilityBar = ({
  handleFilterDrawerOpen,
  handleAddDrawerOpen,
  activeView,
  handleViewToggle,
  filterCount,
  handleClearAllFilter,
}: UtilityBarProps) => {
  const queryClient = useQueryClient();
  const { updateQuarryDatabaseFilters, quarryDatabaseFilters } =
    useQuarryDatabaseContext();
  const { getValues, setValue } = useFormContext();
  const { SyncQuickBooks } = QuarryDatabaseService();
  const { mutateAsync: syncDatabase } = SyncQuickBooks();
  const [syncAction, setSyncAction] = useState<boolean>(false);
  const handleSyncActionStart = () => setSyncAction(true);
  const handleSyncActionEnd = () => setSyncAction(false);
  const handleSyncStart = async () => {
    await syncDatabase();
  };
  const handleSyncCallback = (data) => {
    message.success(data);
    queryClient.invalidateQueries({
      queryKey: [QueryKeys.FETCH_QUARRY_LIST_KEY],
    });
  };

  const addOnAfter = (
    <TNButton
      shape={ButtonShape.DEFAULT}
      size={ButtonSize.SMALL}
      block
      text={
        <img
          className={styles.searchFieldIcon}
          src={SearchIcon}
          alt={utilityBarConsts.SEARCH_ICON_ALT}
        />
      }
      onClick={() => {
        updateQuarryDatabaseFilters({
          search: getValues(utilityBarConsts.SEARCH_FIELD_NAME),
        });
      }}
    />
  );

  useEffect(() => {
    if (
      !!quarryDatabaseFilters?.search &&
      !getValues(utilityBarConsts.SEARCH_FIELD_NAME)
    ) {
      setValue(
        utilityBarConsts.SEARCH_FIELD_NAME,
        quarryDatabaseFilters?.search,
      );
    }
  }, [quarryDatabaseFilters?.search]);

  return (
    <>
      <ConditionalRender visible={syncAction}>
        <SocketLoader
          actionType={socketActionType.SYNC_FOR_QUARRY_DATABASE}
          handleActionCall={handleSyncStart}
          closeRender={handleSyncActionEnd}
          handleCallback={handleSyncCallback}
        />
      </ConditionalRender>
      <div className={styles.headerSection}>
        <div className={styles.leftContainer}>
          <div className={styles.invoiceSearch}>
            <InputField
              type={InputfieldTypes.TEXT}
              name={utilityBarConsts.SEARCH_FIELD_NAME}
              placeholder={utilityBarConsts.SEARCH_PLACEHOLDER}
              addonAfter={addOnAfter}
              onKeyDown={(e) => {
                if (e.key === TriggerType.ENTER) {
                  e.preventDefault();
                  updateQuarryDatabaseFilters({
                    search: getValues(utilityBarConsts.SEARCH_FIELD_NAME),
                  });
                }
              }}
            />
          </div>
          <TNButton
            filterButton
            badgeCount={filterCount}
            shape={ButtonShape.DEFAULT}
            size={ButtonSize.LARGE}
            text="Filter"
            icon={<img src={FilterIcon} />}
            afterTitle
            onClick={handleFilterDrawerOpen}
          />
          {filterCount > 0 && (
            <TNButton
              clearFilterButton
              shape={ButtonShape.ROUND}
              size={ButtonSize.MEDIUM}
              text="Clear All"
              onClick={handleClearAllFilter}
            />
          )}
        </div>
        <div className={styles.rightContainer}>
          <ConditionalRender visible={activeView === ViewListingTypes.LIST}>
            <TNButton
              className={styles.syncButton}
              shape={ButtonShape.DEFAULT}
              size={ButtonSize.LARGE}
              type={ButtonType.DEFAULT}
              onClick={handleSyncActionStart}
              text={utilityBarConsts.SYNC_FROM_QUICKBOOKS}
            />
          </ConditionalRender>
          <TNButton
            className={styles.addQuarryButton}
            shape={ButtonShape.DEFAULT}
            size={ButtonSize.LARGE}
            text={
              <div className={styles.addQuarryText}>
                {utilityBarConsts.ADD_QUARRY_BTN}
              </div>
            }
            onClick={handleAddDrawerOpen}
          />
          <div className={styles.innerViewWrapper}>
            <TNButton
              customStyles={ButtonGroupStyles}
              shape={ButtonShape.DEFAULT}
              size={ButtonSize.LARGE}
              text={
                <div
                  className={clsx(styles.hoverButtonGrp, {
                    [styles.selected]: activeView === ViewListingTypes.LIST,
                  })}
                >
                  <img src={ListViewIcon} />
                </div>
              }
              onClick={() => handleViewToggle?.(ViewListingTypes.LIST)}
            />
            <TNButton
              customStyles={ButtonGroupStyles}
              shape={ButtonShape.DEFAULT}
              size={ButtonSize.LARGE}
              text={
                <div
                  className={clsx(styles.hoverButtonGrp, {
                    [styles.selected]: activeView === ViewListingTypes.MAP,
                  })}
                >
                  <img src={MapViewIcon} />
                </div>
              }
              onClick={() => handleViewToggle?.(ViewListingTypes.MAP)}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default UtilityBar;
